/** Shared marketing copy: steps, benefits, venues and FAQs. */

export interface HowItWorksStep {
  step: number;
  title: string;
  description: string;
}

export const HOW_IT_WORKS: HowItWorksStep[] = [
  {
    step: 1,
    title: 'Choose your LaunchPad',
    description:
      'Pick the tablet or metal cabinet that fits your space and budget — from a $600 countertop tablet up to a 43" floor cabinet.',
  },
  {
    step: 2,
    title: 'We configure and install it',
    description:
      'Your device arrives preconfigured with the LaunchPad marketing app and your venue settings. Professional installation and setup are free of charge.',
  },
  {
    step: 3,
    title: 'Customers tap and sign up',
    description:
      'Your existing foot traffic taps the screen and enters a phone number. They instantly receive an SMS with your signup link and current promotion.',
  },
  {
    step: 4,
    title: 'You get paid weekly',
    description:
      'Every signup attributed to your location earns ongoing residual commissions, paid out weekly.',
  },
];

export interface Benefit {
  title: string;
  description: string;
  icon: 'revenue' | 'setup' | 'support' | 'remote' | 'free' | 'secure';
}

export const BENEFITS: Benefit[] = [
  {
    title: 'Create recurring revenue',
    description:
      'Every LaunchPad turns everyday foot traffic into tracked signups, so your business can earn ongoing residual commissions from qualified activity at your location.',
    icon: 'revenue',
  },
  {
    title: 'We handle everything',
    description:
      'Professional installation, complete setup and configuration, ongoing technical support and weekly commission payments. You supply the power and Wi-Fi.',
    icon: 'setup',
  },
  {
    title: 'The app is free',
    description:
      'The LaunchPad marketing app costs nothing — now and for the life of the device. You pay for hardware and shipping, never for software.',
    icon: 'free',
  },
  {
    title: 'Managed remotely',
    description:
      'Change the advertising, swap promotions and check device health from the dashboard. No site visit required to update what is on screen.',
    icon: 'remote',
  },
  {
    title: 'Built for 24/7 duty',
    description:
      'Commercial-grade hardware in locking metal enclosures, designed to run continuously in demanding retail environments.',
    icon: 'secure',
  },
  {
    title: 'Ongoing support',
    description:
      'Real people to call. We monitor devices remotely, push updates automatically and replace hardware that fails.',
    icon: 'support',
  },
];

export const VENUES: string[] = [
  'Convenience stores',
  'Smoke shops',
  'Bars & lounges',
  'Restaurants',
  'Liquor stores',
  'Nightclubs',
  'Entertainment venues',
  'Retail businesses',
];

export const BUILT_FOR: string[] = [
  'iGaming platforms',
  'Marketing partners',
  'Traffic partners',
  'Advertising agencies',
  'High-traffic locations',
];

export interface Faq {
  question: string;
  answer: string;
}

export const FAQS: Faq[] = [
  {
    question: 'What does it cost to get started?',
    answer:
      'Hardware only. LaunchPad tablets start at $600 plus shipping, the 21.5" Street Cabinet is $2,500 plus shipping and the 43" Boomer Cabinet is $3,500 plus shipping. The LaunchPad app, configuration and installation are free.',
  },
  {
    question: 'Is shipping included in the price?',
    answer:
      'No. Shipping is quoted separately based on your location and the model you choose, and is confirmed before you pay anything.',
  },
  {
    question: 'How do I earn money from a LaunchPad?',
    answer:
      'Each device tracks signups from your venue. When customers sign up and engage with the promotion on screen, you earn residual commissions on qualified activity, paid weekly.',
  },
  {
    question: 'What do I need to install one?',
    answer:
      'A power outlet and a Wi-Fi or wired internet connection. Everything else — mounting, configuration and testing — is handled during installation.',
  },
  {
    question: 'Can I advertise my own platform on it?',
    answer:
      'Yes. If you run a platform or product you want in front of real foot traffic, we can load your creative and links onto the LaunchPad network. Get in touch and tell us what you have.',
  },
  {
    question: 'Where do I log in to manage my devices?',
    answer: 'Sign in here on nemolaunchpad.com with your operator account to manage your LaunchPads.',
  },
];
